import React, { useCallback, useMemo, useState } from "react";
import { useDataProvider, useNotify, useRefresh, Button } from "react-admin";
import { Grid, MenuItem, TextField, Typography, InputLabel, Box } from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

import ConfirmModal from "server/components/Modals/ConfirmModal";
import SecuredComponent from "server/security/SecuredComponent";

import { StatusExamsChoices } from "utils/choices";

import { StudentExamStatus } from "utils/constants";
import { specialistRequests } from "api/specialistApi";

import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import permissions from "app/permissions";
import { settings } from "app";
import { accountRequests } from "api/accountApi";
import { employeeRequests } from "api/employeeApi";
import { useNotifyError } from "utils/useNotifyError";

const isValidGrade = grade => {
  const num = Number(grade);
  return Number.isInteger(num) && num >= 2 && num <= 6;
};

const getValidation = (status, grade) => {
  switch (status) {
    case StudentExamStatus.Attended:
      if (grade === "" || grade === null || grade === undefined) {
        return { error: true, helperText: "Моля, въведете оценка." };
      }
      if (!isValidGrade(grade)) {
        return { error: true, helperText: "Оценката трябва да е 2–6." };
      }
      return { error: false, helperText: "" };

    case StudentExamStatus.Disqualified:
      return +grade === 2 ? { error: false, helperText: "" } : { error: true, helperText: "Оценката трябва да е 2." };

    case StudentExamStatus.NotAttended:
      return grade ? { error: true, helperText: "Не трябва да има оценка." } : { error: false, helperText: "" };

    default:
      return { error: true, helperText: "Невалиден статус." };
  }
};

const ApplicationRow = ({ application, index, onChange, onUpdate, mode }) => {
  const validation = getValidation(application.status, application.grade);

  const handleStatusChange = newStatus => {
    let grade = "";

    if (newStatus === StudentExamStatus.Disqualified) {
      grade = 2;
    }

    onChange(index, {
      status: newStatus,
      grade,
    });
  };

  const isEditDisabled = validation.error;

  return (
    <Grid
      container
      spacing={2}
      sx={{ borderBottom: "1px solid #ddd" }}
      mb={2}
      pb={2}
    >
      {/* Index */}
      <Grid
        item
        xs={12}
        md={1}
        sx={settings.style.fieldStyle}
      >
        <Typography sx={settings.style.indexStyle}>{index + 1}</Typography>
      </Grid>

      {/* Specialist */}
      <Grid
        item
        xs={12}
        md={3}
        sx={settings.style.fieldStyle}
      >
        <InputLabel sx={settings.style.dialogInputLabelStyle}>
          Специализант
          <Typography color="black">{application.userName}</Typography>
        </InputLabel>
      </Grid>

      {/* Status */}
      <Grid
        item
        xs={12}
        md={2}
      >
        <TextField
          value={application.status || ""}
          select
          fullWidth
          label="Статус"
          variant="filled"
          error={!application.status}
          helperText={!application.status ? "Моля, изберете статус." : ""}
          onChange={e => handleStatusChange(+e.target.value)}
          sx={{
            "& .MuiSelect-select": {
              textAlign: "start",
            },
          }}
        >
          {StatusExamsChoices.map(choice => (
            <MenuItem
              key={choice.id}
              value={choice.id}
            >
              {choice.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Grade */}
      <Grid
        item
        xs={12}
        md={2}
      >
        <TextField
          value={application.grade}
          type="number"
          label="Оценка"
          variant="filled"
          disabled={application.status !== StudentExamStatus.Attended}
          error={validation.error}
          helperText={validation.helperText}
          InputProps={{
            inputProps: {
              max: 6,
              min: 2,
              onWheel: e => e.target.blur(),
            },
          }}
          onChange={e =>
            onChange(index, {
              grade: e.target.value === "" ? "" : +e.target.value,
            })
          }
        />
      </Grid>

      {/* Employee */}
      <Grid
        item
        xs={12}
        md={3}
        sx={settings.style.fieldStyle}
      >
        <InputLabel sx={settings.style.dialogInputLabelStyle}>
          Служител
          <Typography color="black">{application.employeeName}</Typography>
        </InputLabel>
      </Grid>

      {/* Row Save */}
      {mode === "edit" && (
        <SecuredComponent permission={permissions.canManageSpecialistCampaignsExamResults}>
          <Grid
            item
            xs={12}
            md={1}
            sx={{ margin: "auto" }}
          >
            <Button
              onClick={() => onUpdate(application)}
              variant="contained"
              label="Запази"
              startIcon={<SaveIcon />}
              disabled={isEditDisabled}
            />
          </Grid>
        </SecuredComponent>
      )}
    </Grid>
  );
};

const GradesAndStatusModal = ({ campaignId, examId }) => {
  const [applicationsData, setApplicationsData] = useState([]);
  const [mode, setMode] = useState("create");

  const notifyError = useNotifyError();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const resetState = () => {
    setApplicationsData([]);
    setMode("create");
  };

  const fetchApplications = useCallback(async () => {
    try {
      const response = await dataProvider.customRequest(
        specialistRequests.getCampaignExamApplications(campaignId, examId),
      );

      const applications = response?.data || [];
      if (applications?.length === 0) return;
      const userIds = [...new Set(applications.map(a => a?.candidateUserId).filter(id => id != null))];

      const employeeIds = [...new Set(applications.map(a => a?.employeeId).filter(id => id != null))];

      const [usersRes, employeesRes] = await Promise.all([
        userIds.length
          ? dataProvider.customRequest(accountRequests.getUsersByIdsSelect(userIds))
          : Promise.resolve({ data: [] }),

        employeeIds.length
          ? dataProvider.customRequest(employeeRequests.getProfessorsByIdsSelect(employeeIds))
          : Promise.resolve({ data: [] }),
      ]);

      const usersMap = Object.fromEntries((usersRes?.data || []).map(u => [u.id, u]));

      const employeesMap = Object.fromEntries((employeesRes?.data || []).map(e => [e.id, e]));

      const mapped = applications.map(app => {
        const user = usersMap[app.candidateUserId];
        const employee = employeesMap[app.employeeId];

        return {
          userName: user ? `${user.firstName} ${user.lastName}` : "Няма име",
          employeeName: employee ? `${employee.firstName} ${employee.lastName}` : "Няма име",
          status: app.result?.status ?? StudentExamStatus.Attended,
          grade: app.result?.grade ?? "",
          applicationId: app.id,
        };
      });

      const hasGrades = mapped.some(a => a.grade !== "" && a.grade !== null);

      setMode(hasGrades ? "edit" : "create");
      setApplicationsData(mapped);
    } catch (error) {
      notifyError(error);
      return false;
    }
  }, [campaignId, examId, dataProvider, notifyError]);

  const handleChange = (index, changes) => {
    setApplicationsData(prev => prev.map((item, i) => (i === index ? { ...item, ...changes } : item)));
  };

  const isSaveDisabled = useMemo(() => {
    if (applicationsData.length === 0) return true;
    return applicationsData.some(app => getValidation(app.status, app.grade).error);
  }, [applicationsData]);

  const handleSave = async () => {
    const body = applicationsData.map(a => ({
      status: a.status,
      grade: a.grade === "" ? null : a.grade,
      applicationId: a.applicationId,
    }));

    await dataProvider.customRequest(
      specialistRequests.postCampaignExamResults(campaignId, examId),
      { items: body },
      "POST",
    );

    notify("Успешно записано", { type: "success" });
    refresh();
  };

  const handleUpdate = async application => {
    try {
      await dataProvider.customRequest(
        specialistRequests.patchCampaignExamApplicationResult(campaignId, examId, application.applicationId),
        {
          status: application.status,
          grade: application.grade === "" ? null : application.grade,
        },
        "PATCH",
      );

      notify("Успешно обновено", { type: "success" });
      await fetchApplications();
    } catch (error) {
      notifyError(error);
    }
  };

  return (
    <ConfirmModal
      titleContent={`${mode === "edit" ? "Промяна" : "Добавяне"} на статус и оценки`}
      titleButton="Оценки"
      startIcon={mode === "edit" ? <EditIcon /> : <AddIcon />}
      onOpen={fetchApplications}
      onConfirm={handleSave}
      resetProps={resetState}
      saveTitle="Запази"
      closeTitle="Откажи"
      dialogSize="lg"
      confirmEnabled={
        !isSaveDisabled && checkPermission(getPermissions(), permissions.canManageSpecialistCampaignsExamResults)
      }
      isConfirmButton={mode === "create"}
      triggerButton={
        <Button
          color="primary"
          label="Оценки"
          variant="contained"
          startIcon={mode === "edit" ? <EditIcon /> : <AddIcon />}
          disabled={!checkPermission(getPermissions(), permissions.canPreviewSpecialistsCampaignExamResults)}
        />
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: 2,
          mt: 1,
        }}
      >
        {applicationsData.length > 0 ? (
          applicationsData.map((app, idx) => (
            <ApplicationRow
              key={app.applicationId}
              application={app}
              index={idx}
              onChange={handleChange}
              onUpdate={handleUpdate}
              mode={mode}
            />
          ))
        ) : (
          <Typography
            variant="h6"
            sx={{ margin: "2rem auto" }}
          >
            Няма намерени резултати
          </Typography>
        )}
      </Box>
    </ConfirmModal>
  );
};

export default GradesAndStatusModal;
